/* Created by Interactive 63 - https://interactive63.com */
/**
 * Reversing the timing of selected keyframes, and mirroring easings so each segment plays back
 * with the mirrored curve of its counterpart.
 */
import type { Easing, Keyframe, Project } from '../model/types';
import { reversedHandles } from './motion-curve';

/** Mirrors an easing for reversed playback. Only keyword in/out and cubic curves change. */
export function mirrorEasing(easing: Easing): Easing {
  if (easing.type === 'keyword') {
    if (easing.name === 'ease-in') return { type: 'keyword', name: 'ease-out' };
    if (easing.name === 'ease-out') return { type: 'keyword', name: 'ease-in' };
    return easing;
  }
  if (easing.type === 'cubic') {
    return {
      type: 'cubic',
      x1: 1 - easing.x2,
      y1: 1 - easing.y2,
      x2: 1 - easing.x1,
      y2: 1 - easing.y1,
    };
  }
  return easing;
}

/**
 * Mirrors the times of the selected keyframes within their own span on each track, so the motion
 * plays backwards. Values stay with their keyframes. Tracks with fewer than two selected
 * keyframes are left alone. Where a mirrored keyframe lands on an unselected one, it replaces it.
 */
export function reverseKeyframeTiming(project: Project, keyframeIds: readonly string[]): void {
  const selected = new Set(keyframeIds);
  for (const track of project.tracks) {
    const chosen = track.keyframes.filter((kf) => selected.has(kf.id));
    if (chosen.length < 2) continue;
    const first = chosen[0];
    const last = chosen[chosen.length - 1];
    if (!first || !last) continue;
    const span = first.time + last.time;

    // Outgoing easings: each reversed segment takes the mirror of the segment it replaces.
    const easings = chosen.map((_, j) => {
      const source = chosen[chosen.length - 2 - j];
      return j < chosen.length - 1 && source ? mirrorEasing(source.easing) : null;
    });
    const ordered: Keyframe[] = [...chosen].reverse();
    ordered.forEach((kf, j) => {
      kf.time = span - kf.time;
      const easing = easings[j];
      if (easing) kf.easing = easing;
      // Played backwards, the path arrives where it used to leave.
      if (kf.motion) kf.motion = reversedHandles(kf.motion);
    });

    const byTime = new Map<number, Keyframe>();
    for (const kf of track.keyframes) if (!selected.has(kf.id)) byTime.set(kf.time, kf);
    for (const kf of ordered) byTime.set(kf.time, kf);
    track.keyframes = [...byTime.values()].sort((a, b) => a.time - b.time);
  }
}
